// Copyright (c) 2021, Comsearch, Inc., All rights reserved

import { useCallback, useEffect, useRef, useState } from "react";
import { hide } from "react-notification-system-redux";
import { useDispatch } from "react-redux";
import { fetchMailingListJobById } from "helpers/api/mailingListExport";
import handleError from "../useJobPollingNotification/helpers/handleError";
import handleWarning from "../useJobPollingNotification/helpers/handleWarning";

// NOTE - this hook is meant to be used with a job notification but doesn't necessarily
// need to be within a notification.

// it takes Job information and returns the jobStatus as it is polling for updates.
// it will call handleJobDoneCallback once the jobStatus is "Done"

// NOTE - even if initialJobStatus is "Done", this will still call the API so that it has the
// latest jobData for the given exportJobId

/**
 * hook to poll for job status when it is not yet Done or Error
 * @param {number} exportJobId - id from the API to track the job itself
 * @param {string} initialJobStatus - initial job status to print before polling begins
 * @param {function} handleJobDoneCallback - callback which fires when the jobStatus is finally Done
 * @param {string} uid - id for the notification which called this hook (used to hide the notification depending on API responses)
 * @returns {string} jobStatus - the latest jobStatus from the API
 */

const useJobPollingNotificationForFinalizationMailingList = (
  exportJobId,
  initialJobStatus,
  handleJobDoneCallback,
  uid
) => {
  const dispatch = useDispatch();

  const [jobStatus, setJobStatus] = useState(initialJobStatus);

  const __fetchErrorCount = useRef(0);
  const __timeoutId = useRef(null);

  const ignoreErrorAndEnumerateErrorCount = useCallback(({ status }) => {
    // first off, add one to the error count since we're already handling an error
    __fetchErrorCount.current += 1;

    const ignoreError =
      // allows for 10 successive 500 level errors but any other scenario,
      // will tell the catch handler to not ignore.
      __fetchErrorCount.current < 10 &&
      (status === 502 || status === 503 || status === 504);

    if (ignoreError) {
      __timeoutId.current = setTimeout(checkJobStatus, 3000);
    }

    return ignoreError;
  }, []);

  // hide this notification.
  const hideMessage = useCallback(() => {
    clearTimeout(__timeoutId.current);
    __timeoutId.current = null;
    dispatch(hide(uid));
  }, []);

  const checkJobStatus = useCallback(async () => {
    try {
      const { entity } = await fetchMailingListJobById(exportJobId);

      const { mailingListJobStatus: newJobStatus, message } = entity;

      // if we got to here then that means the network request was successful.
      // reset this counter.
      if (__fetchErrorCount.current !== 0) {
        __fetchErrorCount.current = 0;
      }

      setJobStatus(() => newJobStatus);

      // if an error happened tell em!
      // NOTE - this is different than an error from the API network error handling
      // because we want to let network failures continue to poll however jobStatus === 'Error'
      // means that the job is done and something went wrong.
      if (newJobStatus.toLowerCase() === "error") {
        dispatch(handleError(uid, message));
        hideMessage();
        return;
      }

      // if the message is a warning, then display the warning to the user in the UI
      if (message.toLowerCase().indexOf("warning") === 0) {
        dispatch(handleWarning(uid, message));
      }

      // if job was successful and is not Done
      if (newJobStatus.toLowerCase() === "done") {
        hideMessage();
        handleJobDoneCallback(entity);
        return;
      }

      // if we're here that means that the job is not yet Done or Error.
      // we will continue to poll until we get one of those job statuses or the component unmounts.
      __timeoutId.current = setTimeout(checkJobStatus, 3000);
    } catch (err) {
      // keep track of how many successive 502/503/504 errors there are
      // if they are fewer than 10, then continue polling.
      if (ignoreErrorAndEnumerateErrorCount(err)) {
        return;
      }

      // if we're here, that means we want to hide the document generation notification and
      // instead display an error notification describing what went wrong.
      dispatch(handleError(uid, err.message));
      hideMessage();
    }
  }, []);

  useEffect(() => {
    // trigger this on mount
    checkJobStatus();

    // in case this component unmounts before the finalization is complete.
    return () => {
      clearTimeout(__timeoutId.current);
    };
  }, []);

  return jobStatus;
};

export default useJobPollingNotificationForFinalizationMailingList;
